import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AbilityService } from '@casl/angular';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpLink } from 'apollo-angular/http';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';

import { resetApolloLocalStorageItems } from 'app/utils/apolo-storage-helpers';
import { createApollo, GraphQLModule } from 'graphql/graphql.module';
import { EnvironmentSettingsService } from 'shared/services/environment-settings.service';
import { SessionService } from 'shared/services/session.service';
import { ThemeService } from 'shared/services/theme.service';
import { SharedModule } from 'shared/shared.module';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthInterceptorModule } from './interceptors/auth.module';
import { SystemAdminDashboardModule } from './system-admin-dashboard/system-admin-dashboard.module';
import { LoadingInterceptor } from './interceptors/loading-interceptor';
import { APP_ABILITY, appAbility } from './utils/ability.factory';

registerLocaleData(localeDe);

export function initializeKeycloak(
  keycloakService: KeycloakService,
  environmentSettingsService: EnvironmentSettingsService
): Promise<boolean> {
  const settings = environmentSettingsService.settings;

  return keycloakService.init({
    config: {
      url: settings.keycloakConfig.url,
      realm: settings.keycloakConfig.realm,
      clientId: settings.keycloakConfig.clientId
    },
    loadUserProfileAtStartUp: true,
    initOptions: {
      onLoad: 'check-sso',
      checkLoginIframe: false,
      silentCheckSsoRedirectUri: `${window.location.origin}/assets/common/silent-check-sso.html`
    },
    bearerExcludedUrls: []
  });
}

export function initializeKeycloakAndAccount(
  keycloakService: KeycloakService,
  httpLink: HttpLink,
  environmentSettingsService: EnvironmentSettingsService,
  sessionService: SessionService
): () => Promise<any> {
  return async () => {
    const keycloakInitSuccessfull = await initializeKeycloak(keycloakService, environmentSettingsService);

    if (!keycloakInitSuccessfull) {
      resetApolloLocalStorageItems();
      await keycloakService.login();
    }

    createApollo(httpLink, environmentSettingsService);

    const username = keycloakService.getUsername();

    return firstValueFrom(sessionService.setupRole(username));
  };
}

export function initTheme(themeService: ThemeService): any {
  return (): void => themeService.initTheme();
}

@NgModule({ declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        AuthInterceptorModule,
        AppRoutingModule,
        GraphQLModule,
        SharedModule,
        SystemAdminDashboardModule,
        TranslateModule.forRoot({
            defaultLanguage: 'de',
            fallbackLang: 'de',
            loader: provideTranslateHttpLoader({ prefix: "./assets/common/i18n/", suffix: ".json" }), //   Loads translations from /assets/common/i18n/`${language}`.json
        }),
        KeycloakAngularModule], providers: [
        AbilityService,
        { provide: LOCALE_ID, useValue: 'de-DE' },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeKeycloakAndAccount,
            multi: true,
            deps: [
                KeycloakService,
                HttpLink,
                EnvironmentSettingsService,
                SessionService
            ]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initTheme,
            multi: true,
            deps: [ThemeService]
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoadingInterceptor,
            multi: true
        },
        { provide: APP_ABILITY, useValue: appAbility },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
